// src/App.js - Updated with patient selection for therapists
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import Login from './components/Login';
import { initGA4 } from './utils/analytics';
import TherapistRegister from './components/TherapistRegister'; // Therapist-only registration component
import PasswordResetConfirm from './components/PasswordResetConfirm';
import EmailVerification from './components/EmailVerification';
import OnboardingFlow from './components/OnboardingFlow'; // NEW: Alternative onboarding
import ChatWithSecurity from './components/ChatWithSecurity'; // Use ultra-secure chat
import Sessions from './components/Sessions';
import PatientSelector from './components/PatientSelector';
import { TherapistConnection, PatientInvitations } from './components/TherapistConnection';
import Button from './components/Button';
import Dropdown from './components/Dropdown';
import Icon from './components/Icon';
import Logo from './components/Logo';
import TherapyDashboard from './components/TherapyDashboard';
import PatientDetails from './components/PatientDetails';
import Help from './components/Help';
import TherapistLandingPage from './pages/TherapistLandingPage';
import MobileMenu from './components/MobileMenu';
import PatientDashboard from './components/PatientDashboard';
import Profile from './components/Profile';
import CookieConsent from './components/CookieConsent';
import ConnectTherapist from './components/ConnectTherapist';
import InviteHandler from './components/InviteHandler';
import InstantChat from './components/InstantChat'; // Add InstantChat component
import ExpressSignup from './components/ExpressSignup'; // Add ExpressSignup component
import DesignSystemDemo from './components/DesignSystemDemo'; // Add DesignSystemDemo component
import ContactPage from './pages/ContactPage'; // Add ContactPage component
import HelpPage from './pages/HelpPage'; // Add HelpPage component
import PrivacyPage from './pages/PrivacyPage'; // Add PrivacyPage component
import TermsPage from './pages/TermsPage'; // Add TermsPage component
import WorkLifeBalancePage from './pages/WorkLifeBalancePage'; // Add WorkLifeBalance landing page
import TherapistsPage from './pages/TherapistsPage'; // Add Therapists page
import TherapistBioPage from './pages/TherapistBioPage'; // Add individual therapist bio page
import PaymentSuccess from './pages/PaymentSuccess'; // Payment success page
import PaymentCancelled from './pages/PaymentCancelled'; // Payment cancelled page
import Footer from './components/Footer'; // Add Footer component
import CreditBalance from './components/CreditBalance'; // Credit system display
import PaywallModal from './components/PaywallModal'; // Paywall modal for credit purchases
import { GoogleOAuthProvider } from '@react-oauth/google';
import EnterpriseInviteAccept from './pages/EnterpriseInviteAccept';

// Life Theme Identifier components
import LifeThemeIdentifierPage from './pages/LifeThemeIdentifierPage';
import LifeThemeExpressPage from './pages/LifeThemeExpressPage';
import ThemeQuestionFlow from './pages/ThemeQuestionFlow';
import QuestionDetail from './components/LifeThemeIdentifier/QuestionDetail';
import ThemeSummary from './components/LifeThemeIdentifier/ThemeSummary';
import ThemeOverview from './components/LifeThemeIdentifier/ThemeOverview';
import ThemeProgress from './components/LifeThemeIdentifier/ThemeProgress';
import SymptomSelector from './components/SymptomSelector/SymptomSelector';

// Main App component with router
function App() {
  useEffect(() => {
    // Initialize Google Analytics 4 when app loads
    initGA4();
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Router>
        <div className="min-h-screen ai-bg">
          <AppContent />
        </div>
      </Router>
    </GoogleOAuthProvider>
  );
}

// App content component
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const creditBalanceRef = useRef(null);
  const [token, setToken] = useState(() => {
    // Initialize token from localStorage on app start
    return localStorage.getItem('therapy_auth_token');
  });
  const [user, setUser] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patients, setPatients] = useState([]); // Add patients cache
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [currentCredits, setCurrentCredits] = useState(0);

  // Debug: Log current route
  useEffect(() => {
    console.log('📍 Current route:', location.pathname);
    console.log('📍 Route state:', location.state);
    console.log('📍 Selected patient:', selectedPatient?.id);
    console.log('🔑 Token exists:', !!token);
    console.log('👤 User exists:', !!user);
    console.log('👤 User role:', user?.role);
  }, [location, selectedPatient, token, user]);

  // Load user info when token is available
  useEffect(() => {
    if (token && !user) {
      loadUserInfo();
    }
  }, [token, user]);

  // Load patients when user is therapist
  useEffect(() => {
    if (user && user.role === 'therapist' && token) {
      loadPatients();
    }
  }, [user, token]);

  const loadUserInfo = async () => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        console.log('✅ User info loaded:', userData);
      } else {
        console.error('Failed to load user info');
        handleLogout();
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      handleLogout();
    }
  };

  const loadPatients = async () => {
    try {
      console.log('🔄 Loading patients for caching...');
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${API_BASE}/api/invitations/my-patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
        console.log('✅ Patients cached:', data.patients?.length || 0);
      } else if (response.status === 401 || response.status === 403) {
        // Session expired, logout user
        console.log('🔒 Session expired, logging out user');
        handleLogout();
      }
    } catch (error) {
      console.error('❌ Error loading patients:', error);
    }
  };

  const handleLogin = (authToken, userData = null, returnTo = null) => {
    setToken(authToken);
    // Persist token to localStorage
    localStorage.setItem('therapy_auth_token', authToken);

    // If user arrived via an enterprise invite, silently link the account
    const pendingInviteToken = sessionStorage.getItem('enterprise_invite_token');
    if (pendingInviteToken) {
      sessionStorage.removeItem('enterprise_invite_token');
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/enterprise/link-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ invite_token: pendingInviteToken }),
      })
        .then(res => res.json())
        .then(data => console.log('✅ Enterprise invite linked:', data.organization_name))
        .catch(err => console.warn('⚠️ Could not link enterprise invite (non-critical):', err));
    }
    
    if (userData) {
      setUser(userData);
    }
    
    // Check if there's a returnTo path provided or in location state
    const redirectPath = returnTo || location.state?.returnTo;
    
    if (redirectPath) {
      // Clear the location state and navigate to the returnTo path
      navigate(redirectPath, { replace: true });
    } else {
      // Navigate to appropriate dashboard after login
      if (userData?.role === 'therapist') {
        navigate('/therapist');
      } else {
        navigate('/client');
      }
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setSelectedPatient(null);
    setPatients([]);
    // Remove token from localStorage
    localStorage.removeItem('therapy_auth_token');
    navigate('/');
  };

  const handlePatientSelect = (patient) => {
    console.log('🔄 Selecting patient from dashboard:', patient);
    setSelectedPatient(patient);
    navigate(`/therapist/client/${patient.id}`);
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
    navigate('/therapist');
  };

  const handleViewChat = (patient) => {
    setSelectedPatient(patient);
    navigate(`/therapist/client/${patient.id}/chat`);
  };

  const handleViewSessions = (patient) => {
    setSelectedPatient(patient);
    navigate(`/therapist/client/${patient.id}/sessions`);
  };

  // Component to load patient data for routes
  const PatientRoute = ({ children }) => {
    const { patientId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      const findOrLoadPatient = async () => {
        if (!patientId || !token) {
          console.log('❌ Missing patientId or token:', { patientId, hasToken: !!token });
          return;
        }

        // Check if we already have this patient selected
        if (selectedPatient && String(selectedPatient.id) === String(patientId)) {
          console.log('✅ Patient already selected:', selectedPatient);
          return;
        }

        // Try to find patient in cached patients first
        const cachedPatient = patients.find(p => String(p.id) === String(patientId));
        if (cachedPatient) {
          console.log('✅ Found patient in cache:', cachedPatient);
          setSelectedPatient(cachedPatient);
          setError(null);
          return;
        }

        // If not in cache and we have an empty cache, load patients
        if (patients.length === 0) {
          setLoading(true);
          try {
            console.log('🔄 Loading patients for route:', patientId);
            
            const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const response = await fetch(`${API_BASE}/api/invitations/my-patients`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
              const data = await response.json();
              const allPatients = data.patients || [];
              setPatients(allPatients);
              
              // Find the specific patient
              const patient = allPatients.find(p => String(p.id) === String(patientId));
              
              if (patient) {
                console.log('✅ Patient found after loading:', patient);
                setSelectedPatient(patient);
                setError(null);
              } else {
                console.log('❌ Patient not found in loaded data');
                setError(`Klient med ID ${patientId} hittades inte`);
              }
            } else if (response.status === 401 || response.status === 403) {
              console.log('🔒 Session expired in PatientRoute');
              setError('SESSION_EXPIRED');
            } else {
              console.error('❌ Failed to fetch patients:', response.status);
              setError('Kunde inte ladda klientdata');
            }
          } catch (error) {
            console.error('❌ Network error loading patient:', error);
            setError('Nätverksfel vid laddning av klientdata');
          } finally {
            setLoading(false);
          }
        } else {
          // Patient not found in cache and cache is not empty
          console.log('❌ Patient not found in cache');
          setError(`Klient med ID ${patientId} hittades inte i din klientlista`);
        }
      };

      findOrLoadPatient();
    }, [patientId, token, patients, selectedPatient]);

    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Laddar klientinformation...</p>
            <p className="text-xs text-gray-500 mt-2">Söker efter klient-ID: {patientId}</p>
          </div>
        </div>
      );
    }

    if (error === 'SESSION_EXPIRED') {
      return (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Icon name="alert-triangle" size={48} className="text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Session har gått ut</h3>
            <p className="text-sm text-gray-500 mb-6">
              Din session har gått ut av säkerhetsskäl. Logga in igen för att fortsätta.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  // Clear all state and redirect to login
                  setToken(null);
                  setUser(null);
                  setSelectedPatient(null);
                  setPatients([]);
                  localStorage.removeItem('therapy_auth_token');
                  navigate('/');
                }}
                className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
              >
                <Icon name="user" size={16} className="mr-2" />
                Logga in igen
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Icon name="alert-triangle" size={48} className="text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Kunde inte ladda klientdata</h3>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-xs text-gray-600">
                <strong>Debug info:</strong><br/>
                Klient-ID: {patientId}<br/>
                Token: {token ? 'Tillgänglig' : 'Saknas'}<br/>
                Cached patients: {patients.length}<br/>
                Selected patient: {selectedPatient?.id || 'None'}<br/>
                URL: {window.location.href}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/therapist')}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <Icon name="arrow-left" size={16} className="mr-2" />
                Tillbaka till klientlista
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setSelectedPatient(null);
                  // Force reload patients
                  loadPatients();
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
              >
                <Icon name="refresh" size={16} className="mr-2" />
                Försök igen
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!selectedPatient) {
      return (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <Icon name="user-round" size={48} className="text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Klient hittades inte</h3>
            <p className="text-sm text-gray-500 mb-4">Den begärda klienten kunde inte hittas i din klientlista.</p>
            <p className="text-xs text-gray-500 mb-4">Klient-ID: {patientId}</p>
            <button
              onClick={() => navigate('/therapist')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Icon name="arrow-left" size={16} className="mr-2" />
              Tillbaka till klientlista
            </button>
          </div>
        </div>
      );
    }

    return children;
  };

  // Protected Route Component
  const ProtectedRoute = ({ children, allowedRoles = null }) => {
    // Show loading if we have a token but no user yet
    if (token && !user) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Laddar användarinformation...</p>
          </div>
        </div>
      );
    }

    // Redirect to login if no token
    if (!token) {
      return <Navigate to="/" replace />;
    }

    // Check role-based access (admin has access to all client routes)
    if (allowedRoles && user && !allowedRoles.includes(user.role) && user.role !== 'admin') {
      return user.role === 'therapist' ? 
        <Navigate to="/therapist" replace /> : 
        <Navigate to="/client" replace />;
    }

    return children;
  };

  return (
    <div className="ai-bg">
      {location.pathname !== '/therapist-landing' && location.pathname !== '/contact' && location.pathname !== '/work-life-balance' && location.pathname !== '/design' && (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6">{/* Behåll begränsad bredd för app-sidor */}
            <div className="flex justify-between items-center h-14">
              <Logo 
                onClick={() => {
                  if (!user) {
                    window.location.href = (process.env.REACT_APP_NEXT_URL || 'https://yourtherapist.se') + '/privat';
                  } else {
                    navigate('/');
                  }
                }}
                textColor="text-zink-700"
                hoverTextColor="hover:text-brand-primary"
                className="font-black"
              />
              
              
              {user && (
                <div className="flex items-center space-x-3">
                  {/* Desktop Navigation */}
                  <div className="hidden md:flex items-center space-x-3">
                    {user.role === 'therapist' && (
                      <button
                        onClick={handleBackToPatients} 
                        className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-brand-primary transition-colors"
                      >
                        <Icon name="house" size={16} />
                        <span>Hem</span>
                      </button>
                    )}

                    {user.role === 'patient' && (
                      <button
                        onClick={() => navigate('/client')} 
                        className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-brand-primary transition-colors"
                      >
                        <Icon name="house" size={16} />
                        <span>Hem</span>
                      </button>
                    )}

                    {/* Help Link */}
                    <button
                      onClick={() => navigate('/help')}
                      className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-brand-primary transition-colors"
                    >
                      <Icon name="help-circle" size={16} />
                      <span>Hjälp</span>
                    </button>

                    {/* Credit Balance */}
                    <CreditBalance 
                      token={token} 
                      ref={creditBalanceRef}
                      onClick={(credits) => {
                        setCurrentCredits(credits);
                        setIsPaywallOpen(true);
                      }}
                      onCreditsEmpty={() => {
                        setCurrentCredits(0);
                        setIsPaywallOpen(true);
                      }}
                    />

                    <div className="flex items-center space-x-2 bg-gray-50 rounded-md px-3 py-1">
                      <div className="flex gap-1 items-center">
                        <span className=" text-gray-500">
                          {user.role === 'therapist' ? (
                            <span className="text-red-600 flex items-center gap-1">
                              <Icon name="message-circle-heart" size={14} />
                            </span>
                          ) : (
                            <span className="text-emerald-600 flex items-center gap-1">
                              <Icon name="user" size={14} />
                              Klient
                            </span>
                          )}
                        </span>
                        <span className="font-bold text-gray-900">{user.name}</span>
                      </div>
                      
                      <Dropdown
                        trigger={
                          <button className="flex items-center space-x-1 p-1 rounded hover:bg-gray-200 transition-colors">
                            <Icon name="chevron-down" size={16} className="text-gray-500" />
                          </button>
                        }
                        items={[
                          {
                            label: 'Mitt konto',
                            onClick: () => navigate('/profile'),
                            icon: <Icon name="user-cog" size={16} />
                          },
                          {
                            label: 'Logga ut',
                            onClick: handleLogout,
                            icon: <Icon name="logout" size={16} />
                          }
                        ]}
                        align="right"
                      />
                    </div>
                  </div>

                  {/* Mobile Menu */}
                  <MobileMenu user={user} onLogout={handleLogout} />
                </div>
              )}

              {/* Navigation for non-authenticated users */}
              {!user && location.pathname !== '/' && (
                <div className="flex items-center space-x-3">
                  {/* Desktop Navigation */}
                  <div className="hidden md:flex items-center space-x-3">
                    <button
                      onClick={() => { window.location.href = (process.env.REACT_APP_NEXT_URL || 'https://yourtherapist.se') + '/privat'; }}
                      className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-pink-600 transition-colors"
                    >
                      <Icon name="house" size={16} />
                      <span>Tillbaka hem</span>
                    </button>
                  </div>
                  
                  {/* Mobile Menu */}
                  <MobileMenu />
                </div>
              )}
            </div>
          </div>
        </header>
      )}
      
      <main className={location.pathname === '/therapist-landing' || location.pathname === '/contact' || location.pathname === '/help' || location.pathname === '/work-life-balance' || location.pathname === '/livsteman' || location.pathname === '/design' || location.pathname === '/therapists' || location.pathname === '/express-signup' || location.pathname === '/utforska-livstema' || location.pathname.startsWith('/utforska-livstema/') || location.pathname.startsWith('/therapist/') ? '' : 'max-w-4xl mx-auto w-full px-2 sm:px-6 lg:px-8 py-6 pt-20 mobile-margin-fix'}>
        <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={
              !token ? (
                <Login onLogin={handleLogin} />
              ) : !user ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Laddar användarinformation...</p>
                  </div>
                </div>
              ) : user.role === 'therapist' ? (
                <Navigate to="/therapist" replace />
              ) : (
                <Navigate to="/client" replace />
              )
            } 
          />

          <Route 
            path="/login" 
            element={
              (() => {
                console.log('🔄 /login route evaluation:');
                console.log('  Token exists:', !!token);
                console.log('  User exists:', !!user);
                console.log('  Location state:', location.state);
                
                // If coming from email verification, always show login form regardless of token
                if (location.state?.message && location.state?.message.includes('verifierad')) {
                  console.log('📧 Coming from email verification, showing login form');
                  return <Login onLogin={handleLogin} />;
                }
                
                if (!token) {
                  console.log('🔓 No token, showing login form');
                  return <Login onLogin={handleLogin} />;
                } else if (!user) {
                  console.log('⏳ Token exists but user not loaded, showing loading');
                  return (
                    <div className="flex justify-center items-center py-12">
                      <div className="text-center">
                        <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Laddar användarinformation...</p>
                      </div>
                    </div>
                  );
                } else {
                  console.log('🔄 User has token and user data, redirecting to dashboard');
                  console.log('👤 User role:', user.role);
                  return user.role === 'therapist' ? (
                    <Navigate to="/therapist" replace />
                  ) : (
                    <Navigate to="/client" replace />
                  );
                }
              })()
            } 
          />

          {/* Therapist Landing Page */}
          <Route 
            path="/therapist-landing" 
            element={<TherapistLandingPage />} 
          />

          {/* Work-Life Balance Landing Page */}
          <Route 
            path="/work-life-balance" 
            element={<WorkLifeBalancePage />} 
          />

          {/* Livsteman Landing Page - Redirect to home since it's now the main page */}
          <Route 
            path="/livsteman" 
            element={<Navigate to="/" replace />} 
          />

          {/* Contact Page */}
          <Route 
            path="/contact" 
            element={<ContactPage />} 
          />

          {/* Design System Demo - Public route */}
          <Route 
            path="/design" 
            element={<DesignSystemDemo />} 
          />

          {/* Therapists Pages - Public routes */}
          <Route 
            path="/therapists" 
            element={<TherapistsPage />} 
          />
          <Route 
            path="/therapist/:id" 
            element={<TherapistBioPage />} 
          />

          {/* Help Page */}
          <Route 
            path="/help" 
            element={<HelpPage />} 
          />

          {/* Privacy Page */}
          <Route 
            path="/privacy" 
            element={<PrivacyPage />} 
          />

          {/* Terms Page */}
          <Route 
            path="/terms" 
            element={<TermsPage />} 
          />

          {/* Password Reset Routes */}
          <Route 
            path="/reset-password" 
            element={!token ? <PasswordResetConfirm /> : <Navigate to="/" replace />} 
          />

          {/* Email Verification Routes */}
          <Route 
            path="/verify-email" 
            element={<EmailVerification />} 
          />

          {/* NEW: Alternative Onboarding Flow */}
          <Route 
            path="/onboarding" 
            element={<OnboardingFlow onLogin={handleLogin} />} 
          />

          {/* Therapist Registration - Using TherapistRegister.js component */}
          <Route 
            path="/register" 
            element={
              !token ? (
                <TherapistRegister 
                  onLogin={handleLogin}
                  onRegistrationSuccess={() => {}}
                />
              ) : user?.role === 'therapist' ? (
                <Navigate to="/therapist" replace />
              ) : (
                <Navigate to="/client" replace />
              )
            } 
          />

          <Route 
            path="/therapist-register" 
            element={
              !token ? (
                <TherapistRegister 
                  onLogin={handleLogin}
                  onRegistrationSuccess={() => {}}
                />
              ) : user?.role === 'therapist' ? (
                <Navigate to="/therapist" replace />
              ) : (
                <Navigate to="/client" replace />
              )
            } 
          />

          <Route 
            path="/connect-therapist" 
            element={<ConnectTherapist />} 
          />

          <Route 
            path="/instant-chat" 
            element={<InstantChat />} 
          />

          <Route 
            path="/express-signup" 
            element={<ExpressSignup onLogin={handleLogin} />} 
          />

          <Route 
            path="/invite/:code" 
            element={<InviteHandler />} 
          />

          <Route 
            path="/enterprise-invite/:token" 
            element={<EnterpriseInviteAccept />} 
          />

          <Route 
            path="/invitation-register" 
            element={<ExpressSignup onLogin={handleLogin} />} 
          />

          {/* Payment Success/Cancel Pages */}
          <Route 
            path="/payment-success" 
            element={<PaymentSuccess />} 
          />

          <Route 
            path="/payment-cancelled" 
            element={<PaymentCancelled />} 
          />

          {/* Protected routes */}
          <Route 
            path="/help" 
            element={
              <ProtectedRoute>
                <Help user={user} />
              </ProtectedRoute>
            } 
          />

          {/* Profile route - accessible to both patients and therapists */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute allowedRoles={['patient', 'therapist']}>
                <Profile token={token} user={user} onUserUpdate={setUser} />
              </ProtectedRoute>
            } 
          />

          {/* Client routes */}
          <Route 
            path="/client" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <PatientDashboard token={token} user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/chat" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <ChatWithSecurity 
                  token={token} 
                  onCreditUsed={() => creditBalanceRef.current?.refreshCredits()}
                />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/client/symptom-selector"
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <SymptomSelector token={token} />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/client/sessions" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <Sessions token={token} />
              </ProtectedRoute>
            } 
          />
          {/* Life Theme Identifier routes - accessible to patients */}
          {/* Public express version - no auth required */}
          <Route path="/utforska-livstema" element={<LifeThemeExpressPage />} />
          <Route path="/utforska-livstema/:themeId" element={<ThemeQuestionFlow />} />
          
          {/* Authenticated life theme routes */}
          <Route 
            path="/client/life-themes" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <LifeThemeIdentifierPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/life-themes/overview" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <ThemeOverview />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/life-themes/:themeId/question/:questionId" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <QuestionDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/life-themes/:themeId/summary" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <ThemeSummary />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/life-themes/:themeId/progress" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <ThemeProgress />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/client/life-themes/:themeId/question/:questionId" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <QuestionDetail />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/client/therapist" 
            element={
              <ProtectedRoute allowedRoles={['patient']}>
                <TherapistConnection token={token} userRole={user?.role} />
              </ProtectedRoute>
            } 
          />

          {/* Therapist routes */}
          <Route 
            path="/therapist" 
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <TherapyDashboard 
                  token={token} 
                  onPatientSelect={handlePatientSelect}
                  user={user}
                />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/therapist/invitations" 
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <PatientInvitations token={token} userRole={user?.role} />
              </ProtectedRoute>
            } 
          />

          {/* Client-specific therapist routes */}
          <Route 
            path="/therapist/client/:patientId" 
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <PatientRoute>
                  <PatientDetails
                    patient={selectedPatient}
                    token={token}
                    onBackToList={handleBackToPatients}
                    onViewChat={handleViewChat}
                    onViewSessions={handleViewSessions}
                  />
                </PatientRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/therapist/client/:patientId/chat" 
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <PatientRoute>
                  <ChatWithSecurity 
                    token={token} 
                    patientId={selectedPatient?.id} 
                    isTherapistView={true} 
                    selectedPatient={selectedPatient} 
                  />
                </PatientRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/therapist/client/:patientId/sessions" 
            element={
              <ProtectedRoute allowedRoles={['therapist']}>
                <PatientRoute>
                  <Sessions 
                    token={token} 
                    patientId={selectedPatient?.id} 
                    isTherapistView={true} 
                    selectedPatient={selectedPatient}
                  />
                </PatientRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/therapist/client/:patientId/anamnesis" 
            element={<Navigate to="/therapist" replace />}
          />

          {/* Catch all - redirect to appropriate dashboard */}
          <Route 
            path="*" 
            element={
              <ProtectedRoute>
                {user?.role === 'therapist' ? (
                  <Navigate to="/therapist" replace />
                ) : (
                  <Navigate to="/client" replace />
                )}
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      
      {/* Footer for public pages */}
      {(location.pathname === '/' || location.pathname === '/therapist-landing' || location.pathname === '/contact' || location.pathname === '/help' || location.pathname === '/privacy' || location.pathname === '/terms' || location.pathname === '/instant-chat' || location.pathname === '/work-life-balance' || location.pathname === '/blog' || location.pathname.startsWith('/blog/') || location.pathname === '/therapists' || location.pathname.startsWith('/therapist/')) && (
        <Footer />
      )}
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
      
      {/* PaywallModal */}
      <PaywallModal 
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        credits={currentCredits}
      />
    </div>
  );
}

export default App;